import { FormControlRichTextProps, FormElementControlPropsType } from './index';
import { FormComponentBuilder } from '../index';
import Quill from 'quill';
import styles from './FormRichText.styles';
import { Box, ClickAwayListener, Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import IconMi from 'components/common/icon/IconMi';
import RichTextLinkTooltip from './FormRichTextParts/RichTextLinkTooltip';
import { useDispatch, useSelector } from 'react-redux';
import { setFormElementSharedState } from 'store/formSlice';
import useBundleTranslation from 'i18n';
import RichTextCommandMenu from 'components/common/form/layout/control/FormRichTextParts/RichTextCommandMenu';
import RichTextImageTooltip from './FormRichTextParts/RichTextImageTooltip';
import RichTextColorPickerTooltip from './FormRichTextParts/RichTextColorPickerTooltip';
import striptags from 'striptags';
import { useTheme } from '@mui/material/styles';

const icons: any = Quill.import('ui/icons');
icons['bold'] = renderToString(<IconMi icon={'bold'} />);
icons['italic'] = renderToString(<IconMi icon={'italic'} />);
icons['underline'] = renderToString(<IconMi icon={'underline'} />);
icons['list']['bullet'] = renderToString(<IconMi icon={'sort-list'} />);
icons['list']['ordered'] = renderToString(<IconMi icon={'sort-numbers'} />);
icons['link'] = renderToString(<IconMi icon={'link'} />);
icons['image'] = renderToString(<IconMi icon={'image'} />);
icons['color'] = renderToString(<IconMi icon={'text-color'} />);
icons['background'] = renderToString(<IconMi icon={'bg-color'} />);
icons['align'][''] = renderToString(<IconMi icon={'alignment-left'} />);
icons['align']['center'] = renderToString(<IconMi icon={'alignment-center'} />);
icons['align']['right'] = renderToString(<IconMi icon={'alignment-right'} />);
icons['align']['justify'] = renderToString(<IconMi icon={'alignment-justified'} />);
icons['clean'] = renderToString(<IconMi icon={'clear-formatting'} />);
const Inline: any = Quill.import('blots/inline');
const keyboard: any = Quill.import('modules/keyboard');

class CommandBlot extends Inline {
    static blotName = 'command';
    static tagName = 'span';
    static className = 'quill-command-text';

    static formats(): boolean {
        return true;
    }
}
Quill.register(CommandBlot);

const alignStyle: any = Quill.import('attributors/style/align');
Quill.register(alignStyle, true);

export default function FormRichText({
    controlProps,
    elementProps,
}: FormElementControlPropsType<FormControlRichTextProps>) {
    const { t } = useBundleTranslation(['components/common/rich-text']);
    const initialValue = useRef<string>(controlProps.value);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [clickOutside, setClickOutside] = useState<number>(0);
    const [updateImageHandlerValue, setUpdateImageHandlerValue] = useState<number>(0);
    const richTextContainer = useRef<any>(null);
    const quillRef = useRef<any>(null);
    const quillEditorRef = useRef<any>(null);
    const dispatch = useDispatch();
    const mutationObserver = useRef<any>(null);

    const appTheme = useTheme();
    const brandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties);
    const quillFontSize = useRef<any>([]);
    const quillFontSizeLabels = useRef<any>({});
    const quillFont = useRef<any>([]);
    const quillFontLabels = useRef<any>({});

    //try to detect changes controlProps.value not from editor
    //Like  controlProps.form.hookFormReset();
    const isChangesOutside = useRef<boolean>(false);

    const [showLinkTooltip, setShowLinkTooltip] = useState<false | number>(false);
    const [isActiveImage, setIsActiveImage] = useState<boolean>(false);

    const [currentTextColor, setCurrentTextColor] = useState<string[]>(['', '']);
    const currentTextColorRef = useRef<string[]>(['', '']);
    const updateCurrentTextColor = (value: string[]) => {
        setCurrentTextColor(value);
        currentTextColorRef.current = value;
    };

    const [currentBgColor, setCurrentBgColor] = useState<string[]>(['', '']);
    const currentBgColorRef = useRef<string[]>(['', '']);
    const updateCurrentBgColor = (value: string[]) => {
        setCurrentBgColor(value);
        currentBgColorRef.current = value;
    };

    const ignoreCloseAway = useRef<boolean>(false);
    const [colorPickerData, setColorPickerData] = useState<{
        anchorEl: any;
        value: string;
        valueType: string;
        type: string;
    }>({ anchorEl: null, value: '', valueType: '', type: '' });
    const colorPickerDataRef = useRef<{
        anchorEl: any;
        value: string;
        valueType: string;
        type: string;
    }>({ anchorEl: null, value: '', valueType: '', type: '' });

    const updateColorPickerData = (newData: any) => {
        setColorPickerData(newData);
        colorPickerDataRef.current = newData;
    };
    const closeColorPicker = () => {
        ignoreCloseAway.current = false;
        updateColorPickerData({ ...colorPickerDataRef.current, anchorEl: null });
    };

    const isCommandMenuActive = useRef<boolean>(false);

    const getUsedColors = (type: string, range: any) => {
        const content = quillRef.current.getContents(range.index, range.length);
        let usedColors: string[] = [];
        let isDefault = false;
        const isMixed = content.ops.some((item: any) => {
            if (typeof item.insert === 'string' && item.insert.replaceAll('\n') > '') {
                if (item.attributes && item.attributes[type]) {
                    if (!usedColors.includes(item.attributes[type])) {
                        usedColors.push(item.attributes[type]);
                    }
                } else {
                    isDefault = true;
                }
                return usedColors.length > 1 || (usedColors.length === 1 && isDefault);
            }
            return false;
        });
        if (isMixed) {
            return 'mixed';
        } else if (!isDefault && usedColors.length === 1) {
            return 'single';
        } else {
            return 'default';
        }
    };

    const selectionChangeHandler = (
        range: null | { index: number; length: number },
        oldRange: null | { index: number; length: number },
        source: string,
    ) => {
        if (range) {
            setShowLinkTooltip(false);
            if (range.length == 0) {
                //set cursor without selection (like focus)
                const currentFormat = quillRef.current.getFormat();
                if (currentFormat.link) {
                    setShowLinkTooltip(Date.now());
                }
                //console.log('User cursor is on', range.index);
            } else {
                const currentFormat = quillRef.current.getFormat();

                switch (getUsedColors('background', range)) {
                    case 'mixed':
                        updateCurrentBgColor(['', 'mixed']);
                        break;
                    case 'default':
                        updateCurrentBgColor(['', 'default']);
                        break;
                    case 'single':
                        updateCurrentBgColor([currentFormat.background, '']);
                        break;
                }

                switch (getUsedColors('color', range)) {
                    case 'mixed':
                        updateCurrentTextColor(['', 'mixed']);
                        break;
                    case 'default':
                        updateCurrentTextColor(['', 'default']);
                        break;
                    case 'single':
                        updateCurrentTextColor([currentFormat.color, '']);
                        break;
                }
                //selection range
                //console.log('User has highlighted', quill.getText(range.index, range.length));
            }
        } else {
            //like blur, click out off editor
            //console.log('Cursor not in the editor');
        }
    };

    const textChangeHandler = (delta: any, oldDelta: any, source: string) => {
        isChangesOutside.current = false;
        //hot fix quil 2.0.3 bug with getSemanticHTML .replaceAll(' ', ''&nbsp;'). Revert it
        let formattedValue = quillRef.current.getSemanticHTML().replaceAll('&nbsp;', ' ');
        formattedValue = striptags(formattedValue, ['img']).length > 0 ? formattedValue : '';
        controlProps.onChange(formattedValue);
        //console.log('textChangeHandler', delta, oldDelta, source);
    };

    const showColorPicker = (type: string, currentColor: string[]) => {
        const colorToolbarBtn = richTextContainer.current.querySelector(`.ql-${type}`);
        updateColorPickerData({
            anchorEl: colorToolbarBtn,
            value: currentColor[0],
            valueType: currentColor[1],
            type: type,
        });
    };

    const initFontSizeOptions = () => {
        let quillFontSizeOptions: any[] = [8, 10, 11, 12, 14, 16, 18, 22, 24, 32, 48];
        const defaultIndex = quillFontSizeOptions.findIndex((size) => size == appTheme.typography.fontSize);
        if (defaultIndex !== -1) {
            quillFontSizeOptions[defaultIndex] = false;
        } else {
            quillFontSizeOptions.push(appTheme.typography.fontSize);
            quillFontSizeOptions.sort((a, b) => a - b);
            const defaultIndex = quillFontSizeOptions.findIndex((size) => size == appTheme.typography.fontSize);
            quillFontSizeOptions[defaultIndex] = false;
        }
        quillFontSize.current = quillFontSizeOptions.map((size) => {
            if (size) {
                return size + 'px';
            }
            return size;
        });

        const Size: any = Quill.import('attributors/style/size');
        Size.whitelist = quillFontSize.current.filter((size: any) => !!size);
        Quill.register(Size, true);

        quillFontSizeOptions.forEach((size: any) => {
            if (size) {
                quillFontSizeLabels.current['&[data-value="' + size + 'px"]::before'] = {
                    content: '"' + size + '"',
                };
            } else {
                quillFontSizeLabels.current['&::before'] = {
                    content: '"' + appTheme.typography.fontSize + '"',
                };
            }
        });
    };

    const initFontFamilyOptions = () => {
        let quillFontList: any = [
            {
                name: 'Andale Mono',
                value: 'andale mono, monospace',
            },
            {
                name: 'Arial',
                value: 'arial, helvetica, sans-serif',
            },
            {
                name: 'Arial Black',
                value: 'arial black, sans-serif',
            },
            {
                name: 'Book Antiqua',
                value: 'book antiqua, palatino, serif',
            },
            {
                name: 'Comic Sans MS',
                value: 'comic sans ms, sans-serif',
            },
            {
                name: 'Courier New',
                value: 'courier new, courier, monospace',
            },
            {
                name: 'Georgia',
                value: 'georgia, palatino, serif',
            },
            {
                name: 'Helvetica',
                value: 'helvetica, arial, sans-serif',
            },
            {
                name: 'Impact',
                value: 'impact, sans-serif',
            },
            {
                name: 'Symbol',
                value: 'symbol',
            },
            {
                name: 'Tahoma',
                value: 'tahoma, arial, helvetica, sans-serif',
            },
            {
                name: 'Terminal',
                value: 'terminal, monaco, monospace',
            },
            {
                name: 'Times New Roman',
                value: 'times new roman, times, serif',
            },
            {
                name: 'Trebuchet MS',
                value: 'trebuchet ms, geneva, sans-serif',
            },
            {
                name: 'Verdana',
                value: 'verdana, geneva, sans-serif',
            },
            {
                name: 'Webdings',
                value: 'webdings',
            },
            {
                name: 'Wingdings',
                value: 'wingdings, zapf dingbats',
            },
            {
                name: 'Inter',
                value: 'Inter, Arial, Helvetica, sans-serif',
            },
            {
                name: 'JetBrains Mono',
                value: 'JetBrains Mono, monospace',
            },
        ];

        const regularTextFontId = Number(brandThemeProperties?.usages?.regular_text?.font ?? 0);
        const themeFontsData = brandThemeProperties?.fonts ?? [];
        const regularTextFontData =
            regularTextFontId > 0 && themeFontsData.length > 0
                ? themeFontsData.find((font: any) => font.brand_theme_font_id == regularTextFontId)
                : null;

        if (regularTextFontData) {
            quillFontList.push({
                name: regularTextFontData.font_name,
                value: false,
            });
        } else {
            const defaultFontIndex = quillFontList.findIndex((font: any) => font.name == 'Inter');
            if (defaultFontIndex !== -1) {
                quillFontList[defaultFontIndex].value = false;
            }
        }

        quillFontList = quillFontList.sort((a: any, b: any) => a.name.localeCompare(b.name));
        quillFont.current = Object.values(quillFontList).map((font: any) => font.value);
        const Font: any = Quill.import('attributors/style/font');
        Font.whitelist = quillFont.current.filter((font: any) => !!font);
        Quill.register(Font, true);

        quillFontList.forEach((font: any) => {
            if (font.value) {
                quillFontLabels.current['&[data-value="' + font.value + '"]::before'] = {
                    content: '"' + font.name + '"',
                };
            } else {
                quillFontLabels.current['&::before'] = {
                    content: '"' + font.name + '"',
                };
            }
        });
    };

    useEffect(() => {
        initFontSizeOptions();
        initFontFamilyOptions();

        const defaultHeader = keyboard.DEFAULTS.bindings['header enter'].handler;
        keyboard.DEFAULTS.bindings['header enter'].handler = function (range: any, context: any) {
            //prevent default handler for command menu
            if (!isCommandMenuActive.current) {
                defaultHeader.call(this, range, context);
            }
        };

        let toolbarOptions: any = [
            [{ size: quillFontSize.current }, { font: quillFont.current }, { header: [2, 3, false] }],
            ['bold', 'italic', 'underline'],
        ];
        if (controlProps.settings?.textAlign !== false) {
            toolbarOptions.push([{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }]);
        }
        toolbarOptions = [
            ...toolbarOptions,
            ...[
                ['background', 'color', { list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image', 'clean'],
            ],
        ];

        quillRef.current = new Quill(quillEditorRef.current, {
            readOnly: controlProps.readOnly,
            theme: 'bubble',
            modules: {
                toolbar: toolbarOptions,
                keyboard: {
                    bindings: {
                        'command menu enter': {
                            key: 'Enter',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                        'command menu arrow down': {
                            key: 'ArrowDown',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                        'command menu arrow up': {
                            key: 'ArrowUp',
                            handler: function (range: any, context: any) {
                                return !isCommandMenuActive.current;
                            },
                        },
                        'adjust image handler on undo': {
                            key: 'z',
                            shortKey: true,
                            handler: function (range: any, context: any) {
                                setTimeout(() => {
                                    setUpdateImageHandlerValue(Date.now());
                                }, 0);
                                return true;
                            },
                        },
                        'adjust image handler on redo': {
                            key: ['z', 'Z'],
                            shortKey: true,
                            shiftKey: true,
                            handler: function (range: any, context: any) {
                                setTimeout(() => {
                                    setUpdateImageHandlerValue(Date.now());
                                }, 0);
                                return true;
                            },
                        },
                        'adjust image handler on redo Win': {
                            key: 'y',
                            shortKey: true,
                            handler: function (range: any, context: any) {
                                if (/Win/i.test(navigator.platform)) {
                                    setTimeout(() => {
                                        setUpdateImageHandlerValue(Date.now());
                                    }, 0);
                                }
                                return true;
                            },
                        },
                    },
                },
            },
        });
        quillRef.current.on('selection-change', selectionChangeHandler);
        quillRef.current.on('text-change', textChangeHandler);

        const toolbar = quillRef.current.getModule('toolbar');
        toolbar.addHandler('color', () => {
            if (colorPickerDataRef.current.type == 'color' && colorPickerDataRef.current.anchorEl) {
                closeColorPicker();
            } else {
                if (colorPickerDataRef.current.anchorEl) ignoreCloseAway.current = true;
                showColorPicker('color', currentTextColorRef.current);
            }
        });
        toolbar.addHandler('background', () => {
            if (colorPickerDataRef.current.type == 'background' && colorPickerDataRef.current.anchorEl) {
                closeColorPicker();
            } else {
                if (colorPickerDataRef.current.anchorEl) ignoreCloseAway.current = true;
                showColorPicker('background', currentBgColorRef.current);
            }
        });
        toolbar.addHandler('clean', () => {
            ['background', 'bold', 'color', 'code', 'italic', 'size', 'strike', 'script', 'underline', 'align'].forEach(
                (formatType) => {
                    quillRef.current.format(formatType, false);
                },
            );
        });

        if (richTextContainer.current) {
            const selectIcons = richTextContainer.current.querySelectorAll('.ql-formats .ql-picker-label svg');
            selectIcons.forEach((icon: any) => {
                icon.outerHTML = renderToString(<IconMi icon={'chevron-down'} />);
            });
        }
        if (controlProps.hideControls) {
            const tooltip = document.querySelector('.ql-tooltip') as any;
            if (tooltip) {
                tooltip.style.display = 'none';
            }
        }

        return () => {
            quillRef.current.off('selection-change', selectionChangeHandler);
            quillRef.current.off('text-change', textChangeHandler);
        };
    }, []);

    useEffect(() => {
        if (colorPickerData.anchorEl && !mutationObserver.current) {
            const toolbarEl = richTextContainer.current.querySelector('.ql-bubble > .ql-tooltip');
            if (toolbarEl) {
                mutationObserver.current = new MutationObserver((...arg) => {
                    if (!toolbarEl.checkVisibility()) closeColorPicker();
                });
                mutationObserver.current.observe(toolbarEl, {
                    attributes: true,
                    attributeFilter: ['class'],
                });
            }
        }
        if (!colorPickerData.anchorEl && mutationObserver.current) {
            mutationObserver.current.disconnect();
            mutationObserver.current = null;
        }
    }, [colorPickerData]);

    useEffect(() => {
        if (isChangesOutside.current) {
            const delta = quillRef.current.clipboard.convert({ html: controlProps.value });
            quillRef.current.setContents(delta, 'silent');
        }

        let pureText = '';
        if (quillRef.current) {
            pureText = quillRef.current.getText().trim();
        }

        dispatch(
            setFormElementSharedState({
                formKey: controlProps.form.formKey,
                componentName: controlProps.name,
                sharedState: {
                    pure_text: pureText,
                },
            }),
        );
        isChangesOutside.current = true;
    }, [controlProps.value]);

    useEffect(() => {
        quillRef.current.enable(!controlProps.readOnly);
    }, [controlProps.readOnly]);

    useEffect(() => {
        if (!quillRef.current) {
            return;
        }
        if (controlProps.autoFocus) {
            quillRef.current.focus();
        }
    }, [quillRef.current]);

    return (
        <>
            <ClickAwayListener
                onClickAway={(event: Event | React.SyntheticEvent) => {
                    setShowLinkTooltip(false);
                    setClickOutside(Date.now());
                }}
            >
                <Box
                    sx={{
                        ...{
                            '.ql-bubble .ql-background svg path:first-of-type': {
                                color: currentBgColor[0] ?? undefined,
                            },
                            '.ql-bubble  .ql-color svg path:first-of-type': {
                                color: currentTextColor[0] ?? undefined,
                            },
                            '.ql-bubble .ql-formats .ql-header': {
                                width: '90px',
                            },
                            '.ql-bubble .ql-formats .ql-size': {
                                width: '45px',
                                '.ql-picker-label, .ql-picker-item': quillFontSizeLabels.current,
                            },
                            '.ql-bubble .ql-formats .ql-font': {
                                width: '110px',
                                '.ql-picker-label::before': {
                                    overflow: 'hidden',
                                    maxWidth: '100%',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                                '.ql-picker-item::before': {
                                    whiteSpace: 'normal',
                                },
                                '.ql-picker-label, .ql-picker-item': quillFontLabels.current,
                            },
                        },
                        ...styles.root,
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                    }}
                    className={isFocused || showLinkTooltip || isActiveImage ? 'focused' : undefined}
                    ref={richTextContainer}
                >
                    <Tooltip placement={'top'} title={<Box sx={{ width: '240px' }}>{t('rich_text_icon_tooltip')}</Box>}>
                        <Box sx={styles.richTextIcon}>
                            <IconMi icon={'rich-text'} />
                        </Box>
                    </Tooltip>
                    <Box
                        ref={quillEditorRef}
                        dangerouslySetInnerHTML={{
                            __html: initialValue.current,
                        }}
                    />
                    {quillRef.current && (
                        <>
                            <RichTextLinkTooltip
                                showTooltip={showLinkTooltip}
                                setShowTooltip={setShowLinkTooltip}
                                quill={quillRef.current}
                                richTextContainerRef={richTextContainer}
                            />
                            <RichTextCommandMenu
                                quill={quillRef.current}
                                richTextContainerRef={richTextContainer}
                                onChangeStateCommandMenu={(state) => (isCommandMenuActive.current = state)}
                            />
                            <RichTextImageTooltip
                                setIsActiveImage={setIsActiveImage}
                                clickOutside={clickOutside}
                                updateImageHandler={updateImageHandlerValue}
                                quill={quillRef.current}
                                quillEditorRef={quillEditorRef}
                                enableImageAlign={controlProps.settings?.imageAlign !== false}
                            />
                            <RichTextColorPickerTooltip
                                key={colorPickerData.type + colorPickerData.anchorEl ? 'open' : 'close'}
                                colorPickerAnchorEl={colorPickerData.anchorEl}
                                value={colorPickerData.value}
                                valueType={colorPickerData.valueType}
                                onSetColor={(colorValue: string) => {
                                    closeColorPicker();
                                    quillRef.current.format(colorPickerDataRef.current.type, colorValue);
                                }}
                                onClickAway={() => {
                                    if (ignoreCloseAway.current) {
                                        ignoreCloseAway.current = false;
                                        return;
                                    }
                                    closeColorPicker();
                                }}
                            />
                        </>
                    )}
                </Box>
            </ClickAwayListener>
        </>
    );
}

export class FormRichTextBuilder extends FormComponentBuilder {
    prepareProps(): FormControlRichTextProps {
        return {
            ...this.controlProps,
            autoFocus: this.elementProps.componentProps?.autoFocus ?? false,
            hideControls: this.elementProps.componentProps?.hideControls ?? false,
            settings: this.elementProps.componentProps?.settings ?? {},
        };
    }
}
